import rndm from 'rndm';
import wsProbe from '../../../utils/ws-probe';

function Websocket (evs, bus, clientID, scaledroneKey) {
    if (!(this instanceof Websocket)) {
        return new Websocket(evs, bus, clientID, scaledroneKey);
    }
    this.evs = evs;
    this.drone = null;
    this.clientID = clientID;
    this.scaledroneKey = scaledroneKey;
    this.bus = bus;
    this.connect();
}

Websocket.prototype.connect = function (_drone) {
    // create a scaledrone
    // on drone-close, setup a new drone client
    // pass events to this.bus
    var { bus, evs, clientID } = this;
    var bus = this.bus;
    var evs = this.evs;
    var drone = this.drone =
        _drone || new window.ScaleDrone(this.scaledroneKey);
    var self = this;
    var opened = false;
    var stopHealth;
    var room;

    drone
        .once('open', function (err) {
            if (err) return bus.emit(evs._meta.error, err);

            opened = true;
            room = drone.subscribe(clientID);
            unsub(drone, room);
            room.once('open', function (err) {
                if (err) return bus.emit(evs._meta.error, err);
                bus.emit(evs._meta.open, null);
                _demux(evs, bus, room);
            });

            stopHealth = health({
                room,
                drone,
                clientID,
                onSlowness,
                onReconnect
            });
        })
        .once('close', function (data) {
            if (!opened) return;
            bus.emit(evs._meta.close, data);
            self.connect();
        })
        .on('error', function (err) {
            bus.emit(evs._meta.error, err);
        })

        // User has successfully reconnected after disconnecting
        .on('reconnect', function () {
            console.log('reconnect', arguments);
            wsProbe.sdk('reconnect');
            bus.emit(evs._meta.reconnect, null);
            stopHealth();
            stopHealth = health({
                room,
                drone,
                clientID,
                onSlowness,
                onReconnect,
                isOpen: true
            });
        })

        // User has disconnected and Scaledrone is trying to reconnect
        .on('disconnect', function () {
            console.log('disconnect', arguments);
            wsProbe.sdk('disconnect');
            // bus.emit(evs._meta.trouble, null);
        })
    ;

    function onSlowness () {
        console.log('socket slow', 'doing reconnect/reload')

        wsProbe.banner('trouble');
        bus.emit(evs._meta.trouble, null);
    }

    function onReconnect () {
        console.log('socket reconnect', 'doing reconnect/reload')
        wsProbe.banner('cleared');
        bus.emit(evs._meta.reconnect, null);
    }
};

function unsub (drone, room) {
    drone.once('close', () => room.unsubscribe());
}

// How long a single health round trip may take before it counts as a miss.
// Measured over 57 round trips on a healthy connection: 87ms median, 231ms
// at p99, no outliers. Two seconds leaves roughly 8x headroom, which the
// consecutive-miss rule below widens further -- an isolated slow echo costs
// one miss and nothing else. Deliberately no more generous than that: every
// second here is a second longer that a stale screen keeps looking live.
const ECHO_DEADLINE = 2000;

// Gap between cycles. With the deadline this sets detection latency --
// `deadline + 2 * cycle` at best and one cycle more at worst, so 12-17s.
// That number matters: the banner is what tells an operator the screen may
// be stale, and acting on stale state -- changing an encoder that has
// already started -- is the failure it exists to prevent. Latency is bought
// with cycle length, not by lowering the miss count, so the two can be
// tuned independently.
//
// The health ping is the only traffic on an idle channel (verified: 24
// minutes of a live session carried one 38-second burst of anything else),
// so this interval alone sets the cost. While nobody is looking at the tab
// that spend buys nothing and the staleness argument does not apply either,
// so hidden tabs back off hard.
const INTERVAL_VISIBLE = 3000;
const INTERVAL_HIDDEN = 55000;

// A dropped echo is not news. Only sustained silence is worth interrupting
// someone over, and requiring consecutive misses is what separates the two.
// This is the false-positive guard; it is not the latency knob.
const MISSES_BEFORE_TROUBLE = 3;

function health (opts) {
    
    var {
        drone,
        room,
        clientID,
        onSlowness,
        onReconnect,
        isOpen
    } = opts;

    var closed = false;
    var sessionId = rndm(16);
    var id = 0;
    var lastEcho;
    var trouble;
    var misses = 0;
    var pending = null;
    var pendingId;

    drone.once('close', teardown);

    if (isOpen) ping(id++);
    else {
        room.once('open', function (err) {
            if (err) return;
            ping(id++);
        });
    }

    room.on('data', onData);
    function onData (msg) {
        var type = (msg.action || {}).type;
        if (type !== 'health') return;
        if (msg.data.sessionId !== sessionId) return;

        // Monotonic: a late echo arriving after a newer one must not drag
        // `lastEcho` backwards and manufacture a miss.
        var iteration = msg.data.socketIteration;
        if (lastEcho === undefined || iteration > lastEcho) lastEcho = iteration;
    }

    function interval () {
        try {
            return (typeof document !== 'undefined' && document.hidden)
                ? INTERVAL_HIDDEN
                : INTERVAL_VISIBLE;
        } catch (e) {
            return INTERVAL_VISIBLE;
        }
    }

    // Coming back to a hidden tab should not mean waiting out the long
    // interval before the first probe -- that is exactly the moment someone
    // starts trusting what is on screen again.
    function onVisibility () {
        if (closed) return;
        if (typeof document === 'undefined' || document.hidden) return;
        if (pending === null) return;
        clearTimeout(pending);
        pending = setTimeout(publish, 1000);
    }

    if (typeof document !== 'undefined' && document.addEventListener) {
        document.addEventListener('visibilitychange', onVisibility);
    }

    function checkHealth () {
        if (closed) return;

        if (lastEcho === id - 1) {
            misses = 0;
            if (trouble) {
                trouble = false;
                onReconnect();
            }
        } else {
            misses++;
            if (!trouble && misses >= MISSES_BEFORE_TROUBLE) {
                trouble = true;
                onSlowness();
            }
        }

        // Always continue the loop. Previously a miss rescheduled `checkHealth`
        // without sending anything and without advancing `id`, so a single lost
        // echo left the check comparing against a reply that was never coming --
        // pings stopped entirely and the banner stayed up until Scaledrone
        // happened to reconnect underneath it.
        ping(id++);
    }

    function publish () {
        if (closed) return;

        pending = null;
        setTimeout(checkHealth, ECHO_DEADLINE);
        wsProbe.outbound(sessionId, pendingId);
        drone.publish({
            room: clientID,
            message: {
                action: {
                    method: "health",
                    type: "health"
                },
                data: {
                    sessionId: sessionId,
                    timestamp: Date.now(),
                    socketIteration: pendingId
                }
            }
        });
    }

    function ping (n) {
        if (closed) return;
        pendingId = n;
        pending = setTimeout(publish, interval());
    }

    // Both ways out run this. The caller stops us on an SDK reconnect, but a
    // drone close is rebuilt by `connect`'s own close handler, which calls
    // `self.connect()` without stopping the old loop -- so this is the only
    // cleanup that path ever gets. Leaving it to `stop()` alone leaked a
    // document listener per reconnect, which is once per wake on a browser
    // that sleeps background tabs. Idempotent: both exits may fire.
    function teardown () {
        closed = true;
        if (pending !== null) {
            clearTimeout(pending);
            pending = null;
        }
        room.off('data', onData);
        if (typeof document !== 'undefined' && document.removeEventListener) {
            document.removeEventListener('visibilitychange', onVisibility);
        }
    }

    return teardown;
}

function _demux (evs, bus, room) {
    room.on('data', function (data) {
        /**
         * @NOTE
         * 
         * You can use this to log all incoming WSS message in the console
         */
        // console.log('__WSS__', data);
        
        // filter msgs not formatted right
        var method = ((data || {}).action || {}).method;
        if (!method) {
            if (process.env.NODE_ENV !== 'development') return;
            return console.warn('Invalid websocket message', data);
        }
    
        if (method === 'health') return;
        
        // emit namespaced event name
        var evName = (evs[data.action.method] || {})[data.action.type];
        if (!evName) {
            if (process.env.NODE_ENV !== 'development') return;
            return console.warn('Unknown websocket message', data);
        }
        bus.emit(evName, data);
    });

    return bus;
}

Websocket._demux = _demux; // expose this just so we can do tests
Websocket._health = health; // ditto -- the health loop is tested directly
// Exported so the tests drive the loop on its real timings and cannot drift.
Websocket._healthTimings = {
    ECHO_DEADLINE,
    INTERVAL_VISIBLE,
    INTERVAL_HIDDEN,
    MISSES_BEFORE_TROUBLE
};
export default Websocket;
